import React, { useCallback, useEffect } from 'react';
import { CreateNoteButton } from '@/components/buttons/viewHeaderButtons/CreateNoteButton';
import { TabView } from '@/components/layouts/TabView';
import { CreateTaskButton } from '@/components/buttons/viewHeaderButtons/CreateTaskButton';
import { ClientBrandIcon } from '@/components/icons/ClientBrandIcon';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { getClientTabs } from '@/app/tabs';
import { addRecentlyViewed } from '@/features/userPrefs/userPrefsSlice';
import { useGetClientContactData } from '@/hooks/useGetClientContactData';
import { ClientIcon } from '@/components/icons/Client';
import { ProspectsIcon } from '@/components/icons/ProspectsIcon';
import { useTheme } from '@emotion/react';

const ICON_HEIGHT = '55px';
const ICON_PROPS = {
	height: ICON_HEIGHT,
	width: ICON_HEIGHT,
};

const HEADER_COMPONENTS = {
	notes: CreateNoteButton,
	tasks: CreateTaskButton,
};

export const ClientTabView = () => {
	const theme = useTheme();
	const location = useLocation();
	const { clientId } = useParams();
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const { clientRow } = useObjectContext();
	const isProspect = clientRow?.isProspect;
	const dispatch = useDispatch();

	useEffect(() => {
		if (userDetails?.userId && clientRow?.name) {
			dispatch(
				addRecentlyViewed({
					userId: userDetails.userId,
					type: 'clients',
					data: {
						id: clientRow.id,
						name: clientRow.name,
						path: location.pathname,
						isProspect,
					},
				}),
			);
		}
	}, [userDetails?.userId, clientRow?.name, clientRow?.id, location.pathname, isProspect]);

	const { logoDomain, mainContact, subtitle } = useGetClientContactData({
		clientRow,
		clientId,
	});

	// Prospects have no policies
	const getTabs = useCallback(
		(path) =>
			isProspect
				? getClientTabs(path).filter((tab) => tab.value !== 'policies')
				: getClientTabs(path),
		[isProspect],
	);

	const fallbackIconProps = {
		...ICON_PROPS,
		...(theme.palette.mode === 'dark' && { bgColor: 'transparent' }),
		...(theme.palette.mode !== 'dark' && !isProspect && { bgColor: '#34495E' }),
	};

	return (
		<TabView
			objectType='client'
			getTabs={getTabs}
			headerComponents={HEADER_COMPONENTS}
			icon={
				<ClientBrandIcon
					domain={logoDomain}
					{...ICON_PROPS}
					fallbackIcon={
						isProspect ? (
							<ProspectsIcon {...fallbackIconProps} />
						) : (
							<ClientIcon {...fallbackIconProps} />
						)
					}
				/>
			}
			subtitle={subtitle}
		/>
	);
};
